import { AlertTriangle, Lock, Plus } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import { IndexPath } from '@/Config/app/router/index.path';
import { useCurrentIndexPath } from '@/Config/app/router/useIndexPath.hook';
import {
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/Domain/components/Shared/Sidebar/Sidebar';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { useSubscription } from '@/Domain/components/SubscriptionProvider';
import { BookTextIcon, BookTextIconHandle } from '@/Domain/components/icons/BookTextIcon';
import { FingerprintIcon, FingerprintIconHandle } from '@/Domain/components/icons/FingerprintIcon';
import { ZapIcon, ZapIconHandle } from '@/Domain/components/icons/ZapIcon';
import { PostHogEventEnum, type AgentCreateSource } from '@/Domain/enum/PostHogEvent.enum';
import { useCreateAgent } from '@/Domain/hooks/App/useCreateAgent.hook';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { cn } from '@/Domain/utils/cn';

export const SidebarAgentGroup = ({ isLocked }: { isLocked: boolean }) => {
  const { t } = useTranslation(['sidebar_menu', 'knowledge_base']);
  const currentPath = useCurrentIndexPath();
  const identityIconRef = useRef<FingerprintIconHandle>(null);
  const knowledgeBaseIconRef = useRef<BookTextIconHandle>(null);
  const actionsIconRef = useRef<ZapIconHandle>(null);
  const { getConfig, isFetched, isActivated } = useSubscription();
  const { capture } = usePostHogAnalytics();
  const kbAtLimit = isFetched && getConfig('can_add_article') === false;
  const articlesUsed = getConfig('articles_used') as number | undefined;
  const articlesLimit = getConfig('articles_limit') as number | null | undefined;

  // Optimistically allow while the subscription is still loading, then gate on the plan.
  const canAddBot = !isFetched || isActivated('can_add_bot');
  const { isCreating, createAgent } = useCreateAgent();
  const handleAddAgent = (): void => createAgent('sidebar_add_button' satisfies AgentCreateSource);

  // Sidebar mounts on nearly every page — ref dedupe is load-bearing.
  const wasAtLimit = useRef(false);
  useEffect(() => {
    if (kbAtLimit && !wasAtLimit.current) {
      capture(PostHogEventEnum.KbLimitWarningSidebarViewed, {
        articles_used: articlesUsed,
        articles_limit: articlesLimit,
      });
    }
    wasAtLimit.current = kbAtLimit;
  }, [kbAtLimit, articlesUsed, articlesLimit, capture]);

  const content = (
    <SidebarGroup>
      <SidebarGroupLabel>
        {t('menu.aiAgent.title')}
        {isLocked && <Lock className="ml-auto !size-3" />}
      </SidebarGroupLabel>
      {!isLocked && canAddBot && (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <SidebarGroupAction
                onClick={handleAddAgent}
                disabled={isCreating}
                aria-busy={isCreating}
                aria-label={t('button.add_ai_agent')}
                data-testid="sidebar-add-agent-button"
                className="top-0.5 w-6 cursor-pointer rounded-md border border-border bg-background disabled:cursor-default disabled:opacity-60"
              >
                {isCreating ? <Spinner className="size-4" /> : <Plus />}
              </SidebarGroupAction>
            </TooltipTrigger>
            <TooltipContent side="right">{t('button.add_ai_agent')}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            asChild
            tooltip={t('menu.aiAgent.identity')}
            isActive={currentPath == IndexPath.identity}
            className={cn(isLocked && 'opacity-50 pointer-events-none')}
            onMouseEnter={() => !isLocked && identityIconRef.current?.startAnimation()}
            onMouseLeave={() => !isLocked && identityIconRef.current?.stopAnimation()}
          >
            {isLocked ? (
              <div data-testid="identity">
                <FingerprintIcon size={16} />
                <span>{t('menu.aiAgent.identity')}</span>
              </div>
            ) : (
              <NavLink
                to={IndexPath.identity}
                data-shortcut="4"
                data-testid="identity"
                viewTransition
              >
                <FingerprintIcon ref={identityIconRef} size={16} />
                <span>{t('menu.aiAgent.identity')}</span>
              </NavLink>
            )}
          </SidebarMenuButton>
        </SidebarMenuItem>
        <SidebarMenuItem>
          <SidebarMenuButton
            asChild
            tooltip={t('menu.aiAgent.knowledge_base')}
            isActive={currentPath == IndexPath.knowledgeBase.root}
            className={cn(isLocked && 'opacity-50 pointer-events-none')}
            onMouseEnter={() => !isLocked && knowledgeBaseIconRef.current?.startAnimation()}
            onMouseLeave={() => !isLocked && knowledgeBaseIconRef.current?.stopAnimation()}
          >
            {isLocked ? (
              <div>
                <BookTextIcon size={16} />
                <span>{t('menu.aiAgent.knowledge_base')}</span>
              </div>
            ) : (
              <NavLink to={IndexPath.knowledgeBase.root} data-shortcut="5" viewTransition>
                <BookTextIcon ref={knowledgeBaseIconRef} size={16} />
                <span>{t('menu.aiAgent.knowledge_base')}</span>
                {kbAtLimit && (
                  // `!` overrides sidebarMenuButtonVariants's `[&_*]:text-inherit`.
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <AlertTriangle
                          className="ml-auto !size-4 !text-warning"
                          aria-label={t('knowledge_base:kb_limit_reached')}
                          data-testid="kb-limit-warning-icon"
                        />
                      </TooltipTrigger>
                      <TooltipContent side="right">
                        {t('knowledge_base:kb_limit_reached')}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </NavLink>
            )}
          </SidebarMenuButton>
        </SidebarMenuItem>
        <SidebarMenuItem>
          <SidebarMenuButton
            asChild
            tooltip={t('menu.channels.actions')}
            isActive={currentPath == IndexPath.actions}
            className={cn(isLocked && 'opacity-50 pointer-events-none')}
            onMouseEnter={() => !isLocked && actionsIconRef.current?.startAnimation()}
            onMouseLeave={() => !isLocked && actionsIconRef.current?.stopAnimation()}
          >
            {isLocked ? (
              <div>
                <ZapIcon size={16} />
                <span>{t('menu.channels.actions')}</span>
              </div>
            ) : (
              <NavLink to={IndexPath.actions} data-shortcut="6" viewTransition>
                <ZapIcon ref={actionsIconRef} size={16} />
                <span>{t('menu.channels.actions')}</span>
              </NavLink>
            )}
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarGroup>
  );

  if (isLocked) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div>{content}</div>
          </TooltipTrigger>
          <TooltipContent side="right">{t('tooltips.locked_section')}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return content;
};
